export const HEYDU_DASHBOARD_SESSION_ID_KEY = 'heydu-dashboard-sessionId';
export const HEYDU_DASHBOARD_BEARER_TOKEN_KEY = 'heydu-dashboard-bearerToken';
export const HEYDU_DASHBOARD_REFRESH_TOKEN_KEY = 'heydu-dashboard-refreshToken';
export const HEYDU_DASHBOARD_PROFILE_KEY = 'heydu-dashboard-profile';
export const HEYDU_DASHBOARD_EMAIL_KEY = 'heydu-dashboard-email';
export const HEYDU_DASHBOARD_NAME_KEY = 'heydu-dashboard-name';
export const HEYDU_DASHBOARD_PROFILE_ID_KEY = 'heydu-dashboard-profileId';

const LEGACY_ACCESS_TOKEN_KEY = 'access-token';
const LEGACY_REFRESH_TOKEN_KEY = 'refresh-token';
const LEGACY_PROFILE_KEY = 'profile';
const LEGACY_USER_ID_KEY = 'user-id';
const LEGACY_SESSION_ID_KEY = 'heydu-session-id';

function isBrowser() {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function getCandidateValue(source, keys) {
    if (!source || typeof source !== 'object') return '';
    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (typeof value === 'number' && !Number.isNaN(value)) return String(value);
    }
    return '';
}

function normalizePayload(payload) {
    if (!payload || typeof payload !== 'object') return {};
    return payload;
}

function parseStoredJson(key) {
    if (!isBrowser()) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (_err) {
        return null;
    }
}

function writeBoth(primaryKey, legacyKey, value) {
    if (!isBrowser() || !value) return;
    window.localStorage.setItem(primaryKey, value);
    if (legacyKey) {
        window.localStorage.setItem(legacyKey, value);
    }
}

export function getDashboardAccessToken() {
    if (!isBrowser()) return '';
    return (
        window.localStorage.getItem(HEYDU_DASHBOARD_BEARER_TOKEN_KEY) ||
        window.localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY) ||
        ''
    );
}

export function getDashboardRefreshToken() {
    if (!isBrowser()) return '';
    return (
        window.localStorage.getItem(HEYDU_DASHBOARD_REFRESH_TOKEN_KEY) ||
        window.localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY) ||
        ''
    );
}

export function getDashboardSessionId() {
    if (!isBrowser()) return '';
    return (
        window.localStorage.getItem(HEYDU_DASHBOARD_SESSION_ID_KEY) ||
        window.localStorage.getItem(LEGACY_SESSION_ID_KEY) ||
        ''
    );
}

export function getDashboardProfile() {
    return (
        parseStoredJson(HEYDU_DASHBOARD_PROFILE_KEY) ||
        parseStoredJson(LEGACY_PROFILE_KEY) ||
        {}
    );
}

export function getDashboardEmail() {
    if (!isBrowser()) return '';
    const storedEmail = window.localStorage.getItem(HEYDU_DASHBOARD_EMAIL_KEY);
    if (storedEmail && storedEmail.trim()) return storedEmail.trim().toLowerCase();
    const profile = getDashboardProfile();
    return getCandidateValue(profile, ['email']).toLowerCase();
}

export function getDashboardUserId() {
    if (!isBrowser()) return '';
    const storedId =
        window.localStorage.getItem(HEYDU_DASHBOARD_PROFILE_ID_KEY) ||
        window.localStorage.getItem(LEGACY_USER_ID_KEY) ||
        '';
    if (storedId && storedId.trim()) return storedId.trim();
    const profile = getDashboardProfile();
    return getCandidateValue(profile, ['id']);
}

export function hasDashboardAuthSession() {
    return Boolean(
        getDashboardAccessToken() ||
        getDashboardRefreshToken() ||
        getDashboardSessionId()
    );
}

export function setDashboardSessionId(sessionId) {
    const normalized = String(sessionId || '').trim();
    if (!normalized) return;
    writeBoth(HEYDU_DASHBOARD_SESSION_ID_KEY, LEGACY_SESSION_ID_KEY, normalized);
}

export function saveDashboardAuthSession(payload, fallbackEmail = '') {
    if (!isBrowser()) return;

    const root = normalizePayload(payload);
    const responseData = normalizePayload(root.data);
    const apiData = normalizePayload(responseData.data);
    const businessData = normalizePayload(apiData.data);
    const responseProfile = normalizePayload(responseData.profile);
    const apiProfile = normalizePayload(apiData.profile);
    const businessProfile = normalizePayload(businessData.profile);
    const responseUser = normalizePayload(responseData.user);
    const apiUser = normalizePayload(apiData.user);

    const sessionId =
        getCandidateValue(root, ['sessionId', 'sessionid', 'session_id', 'sid']) ||
        getCandidateValue(responseData, ['sessionId', 'sessionid', 'session_id', 'sid']) ||
        getCandidateValue(apiData, ['sessionId', 'sessionid', 'session_id', 'sid']) ||
        getCandidateValue(businessData, ['sessionId', 'sessionid', 'session_id', 'sid']);

    const bearerToken =
        getCandidateValue(root, ['bearerToken', 'accessToken', 'token', 'jwt']) ||
        getCandidateValue(responseData, ['bearerToken', 'accessToken', 'token', 'jwt']) ||
        getCandidateValue(apiData, ['bearerToken', 'accessToken', 'token', 'jwt']) ||
        getCandidateValue(businessData, ['bearerToken', 'accessToken', 'token', 'jwt']);

    const refreshToken =
        getCandidateValue(root, ['refreshToken', 'refresh_token']) ||
        getCandidateValue(responseData, ['refreshToken', 'refresh_token']) ||
        getCandidateValue(apiData, ['refreshToken', 'refresh_token']) ||
        getCandidateValue(businessData, ['refreshToken', 'refresh_token']);

    const mergedProfile = {
        ...responseUser,
        ...apiUser,
        ...responseProfile,
        ...apiProfile,
        ...businessProfile,
    };

    if (fallbackEmail && !mergedProfile.email) {
        mergedProfile.email = fallbackEmail;
    }

    if (sessionId) {
        setDashboardSessionId(sessionId);
    }
    if (bearerToken) {
        writeBoth(HEYDU_DASHBOARD_BEARER_TOKEN_KEY, LEGACY_ACCESS_TOKEN_KEY, bearerToken);
    }
    if (refreshToken) {
        writeBoth(HEYDU_DASHBOARD_REFRESH_TOKEN_KEY, LEGACY_REFRESH_TOKEN_KEY, refreshToken);
    }

    if (Object.keys(mergedProfile).length > 0) {
        const serialized = JSON.stringify(mergedProfile);
        writeBoth(HEYDU_DASHBOARD_PROFILE_KEY, LEGACY_PROFILE_KEY, serialized);

        const profileId = getCandidateValue(mergedProfile, ['id']);
        const email = getCandidateValue(mergedProfile, ['email']).toLowerCase();
        const name = getCandidateValue(mergedProfile, ['name']);

        if (profileId) {
            writeBoth(HEYDU_DASHBOARD_PROFILE_ID_KEY, LEGACY_USER_ID_KEY, profileId);
        }
        if (email) {
            window.localStorage.setItem(HEYDU_DASHBOARD_EMAIL_KEY, email);
        }
        if (name) {
            window.localStorage.setItem(HEYDU_DASHBOARD_NAME_KEY, name);
        }
    } else if (fallbackEmail) {
        window.localStorage.setItem(HEYDU_DASHBOARD_EMAIL_KEY, fallbackEmail.trim().toLowerCase());
    }
}

export function consumeDashboardTokenFromUrl() {
    if (!isBrowser()) return false;

    const url = new URL(window.location.href);
    const bearerToken =
        getCandidateValue(
            Object.fromEntries(url.searchParams.entries()),
            ['heyduAccessToken', 'accessToken', 'token', 'jwt']
        ) || '';

    if (!bearerToken) return false;

    writeBoth(HEYDU_DASHBOARD_BEARER_TOKEN_KEY, LEGACY_ACCESS_TOKEN_KEY, bearerToken);

    const refreshToken = (url.searchParams.get('refreshToken') || '').trim();
    const email = (url.searchParams.get('email') || '').trim().toLowerCase();

    if (refreshToken) {
        writeBoth(HEYDU_DASHBOARD_REFRESH_TOKEN_KEY, LEGACY_REFRESH_TOKEN_KEY, refreshToken);
    }
    if (email) {
        window.localStorage.setItem(HEYDU_DASHBOARD_EMAIL_KEY, email);
    }

    ['heyduAccessToken', 'accessToken', 'token', 'jwt', 'refreshToken', 'email'].forEach((key) =>
        url.searchParams.delete(key)
    );
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    return true;
}

export function clearDashboardAuthSession() {
    if (!isBrowser()) return;

    [
        HEYDU_DASHBOARD_SESSION_ID_KEY,
        HEYDU_DASHBOARD_BEARER_TOKEN_KEY,
        HEYDU_DASHBOARD_REFRESH_TOKEN_KEY,
        HEYDU_DASHBOARD_PROFILE_KEY,
        HEYDU_DASHBOARD_EMAIL_KEY,
        HEYDU_DASHBOARD_NAME_KEY,
        HEYDU_DASHBOARD_PROFILE_ID_KEY,
        LEGACY_ACCESS_TOKEN_KEY,
        LEGACY_REFRESH_TOKEN_KEY,
        LEGACY_PROFILE_KEY,
        LEGACY_USER_ID_KEY,
        LEGACY_SESSION_ID_KEY,
    ].forEach((key) => window.localStorage.removeItem(key));
}
